import { Box, Typography, useTheme, Menu, MenuItem } from "@mui/material";
import { useEffect, useState } from "react";
import Assets from "../../Component/Common/Assets";
import { useDispatch } from "react-redux";
import usePageLoader from "../../Redux/hooks/usePageLoader";
import useSnackBar from "../../Redux/hooks/useSnackBar";
import { getPoStatusAction } from "../../Redux/Actions/DashboardActions";
import ErrorHandler from "../../lib/errorHandler";
import dayjs from "dayjs";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const legendData = (theme) => [
  { color: theme.palette.lightYellow.secondary, label: "Pending" },
  { color: theme.palette.bgTrendGreen.secondary, label: "Balanced" },
  { color: theme.palette.primary.main, label: "Closed" },
];

// --------------------- Circle Progress Component ---------------------
const CircleProgress = ({ percentage, color, size = 120, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <svg width={size} height={size} className="circular-progress">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#F0F0F0"
        strokeWidth={strokeWidth}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.5s ease-in-out" }}
      />
    </svg>
  );
};

function POStatus() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const setFullPageLoader = usePageLoader();
  const { setSnackBar } = useSnackBar();
  const currentDate = dayjs();

  // --------------------- State ---------------------
  const [selectedMonth, setSelectedMonth] = useState(currentDate.month());
  const [data, setData] = useState([]);
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [yearMenuAnchor, setYearMenuAnchor] = useState(null);

  const financialYears = generateFinancialYears(10);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(financialYears[0]);

  // --------------------- Helpers ---------------------
  function generateFinancialYears(count) {
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month();
    const financialYearStartMonth = 3; // April
    const currentFinancialYearStart =
      currentMonth >= financialYearStartMonth ? currentYear : currentYear - 1;

    return Array.from({ length: count }, (_, i) => {
      const startYear = currentFinancialYearStart - i;
      return `${startYear}-${(startYear + 1).toString().slice(-2)}`;
    });
  }

  function calculateStats() {
    if (!data || data.length < 3) return { total: 0, pendingPercentage: 0 };
    const total = data.reduce((sum, item) => sum + (item?.count || 0), 0);
    const pendingPercentage =
      total > 0 ? Math.round(((data[0]?.count || 0) / total) * 100) : 0;
    return { total, pendingPercentage };
  }

  function getDateRange() {
    const startYear = parseInt(selectedFinancialYear.split("-")[0]);
    const endYear = startYear + 1;
    const financialYearStartMonth = 3; // April

    let startDate, endDate;
    if (selectedMonth >= financialYearStartMonth) {
      startDate = dayjs().year(startYear).month(selectedMonth).startOf("month");
      endDate = dayjs().year(startYear).month(selectedMonth).endOf("month");
    } else {
      startDate = dayjs().year(endYear).month(selectedMonth).startOf("month");
      endDate = dayjs().year(endYear).month(selectedMonth).endOf("month");
    }

    return {
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    };
  }

  // --------------------- Handlers ---------------------
  const handleMonthClick = (event) => setMonthMenuAnchor(event.currentTarget);
  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setMonthMenuAnchor(null);
  };

  const handleYearClick = (event) => setYearMenuAnchor(event.currentTarget);
  const handleFinancialYearSelect = (financialYear) => {
    setSelectedFinancialYear(financialYear);
    setYearMenuAnchor(null);
  };

const getPoStatusData = async () => {
  setFullPageLoader(true);
  const { startDate, endDate } = getDateRange();

  try {
    const res = await dispatch(getPoStatusAction({ startDate, endDate }));
    const ok = ErrorHandler(res, setSnackBar);

    if (ok) {
      setData(res.payload.data.response);
    }
  } catch (err) {
    setSnackBar("error", err.message);
  } finally {
    setFullPageLoader(false);
  }
};

  // --------------------- Effects ---------------------
  useEffect(() => {
    getPoStatusData();
  }, [selectedMonth, selectedFinancialYear]);

  // --------------------- Derived Data ---------------------
  const { pendingPercentage } = calculateStats();
  const legends = legendData(theme);

  // --------------------- Render ---------------------
  return (
    <Box
      sx={{
        height: "352px",
        borderRadius: "20px",
        border: `0.5px solid ${theme.palette.bgWhite.secondary}`,
        background: theme.palette.bgWhite.third,
        padding: "20px 20px",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            color: theme.palette.bgBlack.secondary,
          }}
        >
          PO Status
        </Typography>

        {/* Month + Year Dropdowns */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "14px",
          }}
        >
          {/* Month */}
          <Box
            onClick={handleMonthClick}
            sx={dropdownBoxStyle(theme)}
          >
            <Assets absolutePath src="/assets/img/Calendar.svg" />
            <Typography sx={dropdownTextStyle(theme)}>
              {months[selectedMonth]}
            </Typography>
          </Box>
          <Menu
            anchorEl={monthMenuAnchor}
            open={Boolean(monthMenuAnchor)}
            onClose={() => setMonthMenuAnchor(null)}
          >
            {months.map((month, index) => (
              <MenuItem key={month} onClick={() => handleMonthSelect(index)}>
                {month}
              </MenuItem>
            ))}
          </Menu>

          {/* Year */}
          <Box
            onClick={handleYearClick}
            sx={dropdownBoxStyle(theme)}
          >
            <Assets absolutePath src="/assets/img/Calendar.svg" />
            <Typography sx={dropdownTextStyle(theme)}>
              FY {selectedFinancialYear}
            </Typography>
          </Box>
          <Menu
            anchorEl={yearMenuAnchor}
            open={Boolean(yearMenuAnchor)}
            onClose={() => setYearMenuAnchor(null)}
          >
            {financialYears.map((year) => (
              <MenuItem key={year} onClick={() => handleFinancialYearSelect(year)}>
                FY {year}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {/* Chart + Legends */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {/* Circles */}
        <Box
          sx={{
            position: "relative",
            width: "276px",
            height: "272px",
            maxWidth: "300px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ position: "absolute" }}>
            <CircleProgress percentage={data?.[0]?.percentage} color={legends[0].color} size={180} />
          </Box>
          <Box sx={{ position: "absolute" }}>
            <CircleProgress percentage={data?.[1]?.percentage} color={legends[1].color} size={150} />
          </Box>
          <Box sx={{ position: "absolute" }}>
            <CircleProgress percentage={data?.[2]?.percentage} color={legends[2].color} size={120} />
          </Box>

          {/* Center Text */}
          <Box sx={{ position: "absolute", textAlign: "center" }}>
            <Typography sx={{ fontSize: "32px", fontWeight: 700, color: theme.palette.bgBlack.secondary }}>
              {pendingPercentage}%
            </Typography>
          </Box>
        </Box>

        {/* Legends */}
        <Box sx={{ flex: 1, maxWidth: "104px" }}>
          {legends.map((item, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
              <Box
                sx={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: item.color,
                  mr: 2,
                }}
              />
              <Box>
                <Typography sx={{ fontWeight: 500, fontSize: "14px", color: theme.palette.bgBlack.secondary, mb: 0.5 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "24px", color: theme.palette.bgBlack.secondary }}>
                  {data?.[index]?.count}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// --------------------- Styles ---------------------
const dropdownBoxStyle = (theme) => ({
  display: "flex",
  padding: "6px 16px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "30px",
  border: `1px solid ${theme.palette.bgWhite.secondary}`,
  boxShadow:
    "46px 32px 16px 0 rgba(183, 194, 215, 0.00), 29px 21px 14px 0 rgba(183, 194, 215, 0.01), 16px 12px 12px 0 rgba(183, 194, 215, 0.05), 7px 5px 9px 0 rgba(183, 194, 215, 0.09), 2px 1px 5px 0 rgba(183, 194, 215, 0.10)",
  cursor: "pointer",
});

const dropdownTextStyle = (theme) => ({
  fontSize: "12px",
  fontWeight: 700,
  color: theme.palette.bgDarkGray.grey600,
});

export default POStatus;
